import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Timer, Plus, Trash2, Wand2, TriangleAlert, Printer } from 'lucide-react';
import { schedulePdf, zoneDays, cycleStarts } from '../lib/offerPdf';
import { zonePlan, autoZones, PLANTS, SOILS, headKind } from '../lib/irrigation';
import { portalRoot } from './TourGuide';
import DecIn from './DecIn';

export const openIrrPlan = () => window.dispatchEvent(new Event('giss-irr-plan'));
const f1 = (v) => (Math.round(v * 10) / 10).toString().replace('.', ',');

function Supply({ t, cfg, setIrr }) {
  return (
    <div className="lv-box" data-testid="irr-supply">
      <div className="sub-title">{t.izSupply}</div>
      <div className="mc-grid">
        <label className="pit-num"><span>{t.izBar}</span><DecIn data-testid="irr-bar-input" value={cfg.bar} onNum={(n) => setIrr({ bar: n })} /></label>
        <label className="pit-num"><span>{t.izLpm}</span><DecIn data-testid="irr-lpm-input" value={cfg.lpm} onNum={(n) => setIrr({ lpm: n })} /></label>
        <label className="pit-num"><span>{t.izSoil}</span><select data-testid="irr-soil-select" value={cfg.soil} onChange={(e) => setIrr({ soil: e.target.value })}>{Object.keys(SOILS).map((k) => <option key={k} value={k}>{t['izSoil_' + k]}</option>)}</select></label>
        <label className="pit-num"><span>{t.izStart}</span><input type="time" data-testid="irr-start-input" value={cfg.start} onChange={(e) => setIrr({ start: e.target.value })} /></label>
      </div>
      <p className="muted small-hint" data-testid="irr-measure-hint">{t.izMeasure}</p>
    </div>
  );
}

function ZoneRow({ t, z, setZone, delZone }) {
  return (
    <div className={`irr-zone ${z.warn.length ? 'warn' : ''}`} data-testid={`irr-zone-${z.id || 'free'}`}>
      <div className="row">
        {z.free ? <b className="irr-zname">{t.izFree}</b> : <input data-testid={`irr-zone-name-${z.id}`} value={z.name} onChange={(e) => setZone(z.id, { name: e.target.value })} />}
        <select data-testid={`irr-zone-plant-${z.id || 'free'}`} value={z.plant} disabled={z.free} onChange={(e) => setZone(z.id, { plant: e.target.value })}>{Object.keys(PLANTS).map((k) => <option key={k} value={k}>{t['izPl_' + k]}</option>)}</select>
        {!z.free && <button className="icon-btn danger" data-testid={`irr-zone-del-${z.id}`} onClick={() => delZone(z.id)}><Trash2 size={14} /></button>}
      </div>
      <p className="sw-res" data-testid={`irr-zone-res-${z.id || 'free'}`}>
        {z.heads.length} {t.izHeads} · <b>{f1(z.q)} l/min</b>{z.share != null && ` (${Math.round(z.share * 100)} %)`}{z.pr > 0 && ` · ${f1(z.pr)} mm/h`}
        {z.min > 0 && <> · ⏱ <b data-testid={`irr-zone-min-${z.id || 'free'}`}>{z.cycles > 1 ? `${z.cycles} × ${Math.ceil(z.min / z.cycles)}` : z.min} min</b> · {z.days}× {t.izWeek} ({zoneDays(z.days).map((k) => t.scDays.split(',')[k]).join(' ')}) · {t.izFrom} {cycleStarts(z).join(', ')}</>}
      </p>
      {z.warn.map((w) => <p key={w} className="cover-warn" data-testid={`irr-warn-${w}-${z.id || 'free'}`}><TriangleAlert size={12} /> {t['izW_' + w].replace('{b}', f1(z.needBar)).replace('{n}', z.cycles)}</p>)}
    </div>
  );
}

// irrigation zones with valve, flow check against the supply, mixed-type warnings and run-time plan
export default function IrrPlan({ t, project, updateProject }) {
  const [on, setOn] = useState(false);
  useEffect(() => { const h = () => setOn(true); window.addEventListener('giss-irr-plan', h); return () => window.removeEventListener('giss-irr-plan', h); }, []);
  if (!on) return null;
  const { cfg, rows, heads } = zonePlan(project);
  const setIrr = (p) => updateProject((pr) => ({ ...pr, irr: { ...(pr.irr || {}), ...p } }));
  const zonesOf = (pr) => pr.irr?.zones || [];
  const setZone = (id, p) => updateProject((pr) => ({ ...pr, irr: { ...(pr.irr || {}), zones: zonesOf(pr).map((z) => (z.id === id ? { ...z, ...p } : z)) } }));
  const addZone = () => updateProject((pr) => ({ ...pr, irr: { ...(pr.irr || {}), zones: [...zonesOf(pr), { id: `z${Date.now().toString(36)}`, name: `${t.izZone} ${zonesOf(pr).length + 1}`, plant: 'lawn' }] } }));
  const delZone = (id) => updateProject((pr) => ({ ...pr, irr: { ...(pr.irr || {}), zones: zonesOf(pr).filter((z) => z.id !== id) }, fittings: pr.fittings.map((f) => (f.zone === id ? { ...f, zone: undefined } : f)) }));
  const auto = () => { const r = autoZones(project, t); updateProject((pr) => ({ ...pr, irr: { ...(pr.irr || {}), zones: r.zones }, fittings: pr.fittings.map((f) => (r.map[f.id] ? { ...f, zone: r.map[f.id] } : f)) })); };
  const assign = (fid, zone) => updateProject((pr) => ({ ...pr, fittings: pr.fittings.map((f) => (f.id === fid ? { ...f, zone: zone || undefined } : f)) }));
  return createPortal(
    <div className="editor-backdrop" data-testid="irr-plan-dialog">
      <div className="editor irr-bom">
        <div className="editor-head"><h4><Timer size={18} /> {t.izTitle}</h4><button className="icon-btn" data-testid="irr-plan-close-btn" onClick={() => setOn(false)}><X size={16} /></button></div>
        <Supply t={t} cfg={cfg} setIrr={setIrr} />
        {!heads.length && <p className="empty" data-testid="irr-plan-empty">{t.izEmpty}</p>}
        <div className="row">
          <button className="btn btn-outline btn-sm" data-testid="irr-zone-add-btn" onClick={addZone}><Plus size={14} /> {t.izAdd}</button>
          <button className="btn btn-green btn-sm" data-testid="irr-zone-auto-btn" disabled={!heads.length} onClick={auto}><Wand2 size={14} /> {t.izAuto}</button>
        </div>
        {rows.map((z) => <ZoneRow key={z.id || 'free'} t={t} z={z} setZone={setZone} delZone={delZone} />)}
        {heads.length > 0 && cfg.zones.length > 0 && (
          <details className="lv-box" data-testid="irr-assign-box">
            <summary className="sub-title">{t.izAssign}</summary>
            {heads.map((f) => (
              <label key={f.id} className="row irr-assign" data-testid={`irr-assign-${f.id}`}><span>{f.name} <em className="muted">({t['izK_' + headKind(f)]})</em></span>
                <select data-testid={`irr-assign-select-${f.id}`} value={f.zone || ''} onChange={(e) => assign(f.id, e.target.value)}><option value="">{t.izFree}</option>{cfg.zones.map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}</select>
              </label>
            ))}
          </details>
        )}
        {rows.some((z) => z.min > 0) && <button className="btn btn-dark btn-sm" data-testid="irr-schedule-pdf-btn" onClick={() => schedulePdf(project, t)}><Printer size={14} /> {t.scPdf}</button>}
        <p className="muted small-hint">{t.izHint}</p>
      </div>
    </div>,
    portalRoot(),
  );
}
